import { useEffect, useState, useRef } from "react";
import api, { photoUrl, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input, Textarea, Modal, Confirm, Empty } from "@/pages/admin/managers/ui";
import { Upload, Trash2, Pencil, Star, Move, RefreshCw, Images, X } from "lucide-react";

export default function PhotosManager() {
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [moveId, setMoveId] = useState(null);
  const dragId = useRef(null);
  const replaceRef = useRef(null);
  const [replacePid, setReplacePid] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const pickBulk = (files) => {
    const arr = Array.from(files).map((f) => ({ file: f, preview: URL.createObjectURL(f), category_id: activeCat }));
    setBulk(arr);
  };
  const setBulkCat = (i, cid) => setBulk((b) => b.map((x, idx) => (idx === i ? { ...x, category_id: cid } : x)));
  const setAllCat = (cid) => setBulk((b) => b.map((x) => ({ ...x, category_id: cid })));
  const submitBulk = async () => {
    if (bulk.some((x) => !x.category_id)) return toast.error("Asigna una categoría a todas las fotos.");
    setBulkBusy(true);
    try {
      const groups = {};
      bulk.forEach((x) => { (groups[x.category_id] = groups[x.category_id] || []).push(x.file); });
      let total = 0;
      for (const [cid, files] of Object.entries(groups)) {
        const fd = new FormData();
        fd.append("category_id", cid);
        files.forEach((f) => fd.append("files", f));
        const { data } = await api.post("/admin/photos/upload", fd);
        total += data.created.length;
      }
      toast.success(`${total} fotografía(s) subidas a ${Object.keys(groups).length} categoría(s).`);
      setBulk(null);
      loadPhotos(activeCat);
      loadCats();
    } catch (e) { toast.error(apiError(e)); }
    setBulkBusy(false);
  };

  const loadCats = async () => {
    const { data } = await api.get("/categories?all=true");
    setCategories(data);
    if (!activeCat && data[0]) setActiveCat(data[0].id);
  };
  const loadPhotos = async (cid) => {
    if (!cid) return;
    const { data } = await api.get(`/admin/photos?category_id=${cid}`);
    setPhotos(data);
  };

  useEffect(() => { loadCats(); }, []);
  useEffect(() => { loadPhotos(activeCat); }, [activeCat]);

  const upload = async (files) => {
    if (!activeCat) return toast.error("Selecciona una categoría primero.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("category_id", activeCat);
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/photos/upload", fd);
      toast.success(`${data.created.length} fotografía(s) subidas.`);
      loadPhotos(activeCat);
      loadCats();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/photos/${editing.id}`, {
        title: editing.title, description: editing.description,
        is_published: editing.is_published,
      });
      toast.success("Fotografía actualizada.");
      setEditing(null);
      loadPhotos(activeCat);
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/photos/${confirmId}`);
      toast.success("Fotografía eliminada.");
      setConfirmId(null);
      loadPhotos(activeCat);
      loadCats();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setCover = async (pid) => {
    await api.post(`/admin/categories/${activeCat}/cover/${pid}`);
    toast.success("Portada actualizada.");
    loadCats();
  };

  const moveTo = async (targetCat) => {
    await api.put(`/admin/photos/${moveId}`, { category_id: targetCat });
    toast.success("Fotografía movida.");
    setMoveId(null);
    loadPhotos(activeCat);
    loadCats();
  };

  const doReplace = async (files) => {
    if (!files[0] || !replacePid) return;
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      await api.post(`/admin/photos/${replacePid}/replace`, fd);
      toast.success("Fotografía reemplazada.");
      loadPhotos(activeCat);
    } catch (e) { toast.error(apiError(e)); }
    setReplacePid(null);
  };

  const onDrop = async (targetId) => {
    if (!dragId.current || dragId.current === targetId) return;
    const from = photos.findIndex((p) => p.id === dragId.current);
    const to = photos.findIndex((p) => p.id === targetId);
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setPhotos(next);
    dragId.current = null;
    await api.put("/admin/photos/reorder", { ids: next.map((p) => p.id) });
    toast.success("Orden actualizado.");
  };

  const cover = categories.find((c) => c.id === activeCat)?.cover_photo_id;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((c) => (
          <button
            key={c.id}
            data-testid={`admin-photo-cat-${c.id}`}
            onClick={() => setActiveCat(c.id)}
            className={`px-4 py-2 rounded-md text-sm transition-colors ${activeCat === c.id ? "bg-[#C5A059] text-[#0A0A0A] font-semibold" : "bg-[#18181B] border border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
          >
            {c.name} <span className="opacity-60">({c.photo_count})</span>
          </button>
        ))}
      </div>

      <label data-testid="admin-photo-upload" className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3f3f46] rounded-lg py-12 cursor-pointer hover:border-[#C5A059] transition-colors mb-8 ${uploading ? "opacity-60" : ""}`}>
        <Upload size={30} className="text-[#C5A059]" />
        <p className="mt-3 text-sm text-[#E4E4E7]">{uploading ? "Subiendo…" : "Arrastra o haz clic para subir fotografías"}</p>
        <p className="text-xs text-[#8C8375] mt-1">JPG, PNG o WebP · varias a la vez · máx 15MB</p>
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={uploading} onChange={(e) => upload(e.target.files)} />
      </label>

      <label data-testid="bulk-upload-trigger" className="flex items-center justify-center gap-2 rounded-lg py-4 mb-8 cursor-pointer font-semibold bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E5C887] transition-colors">
        <Images size={18} /> Subida masiva · elegir muchas fotos y repartirlas por categoría
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => { pickBulk(e.target.files); e.target.value = ""; }} />
      </label>

      {photos.length === 0 ? (
        <Empty>No hay fotografías en esta categoría todavía.</Empty>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-photo-${p.id}`}
              draggable
              onDragStart={() => (dragId.current = p.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(p.id)}
              className="group relative rounded-md overflow-hidden bg-[#18181B] border border-[#27272A]"
            >
              <img src={photoUrl(p, true)} alt={p.title} className="w-full aspect-square object-cover" />
              {cover === p.id && <span className="absolute top-2 left-2 bg-[#C5A059] text-[#0A0A0A] text-[10px] font-semibold px-2 py-0.5 rounded-full">Portada</span>}
              {!p.is_published && <span className="absolute top-2 right-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-full">Oculta</span>}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                <div className="flex gap-1.5">
                  <IconBtn testid={`photo-edit-${p.id}`} onClick={() => setEditing(p)} title="Editar"><Pencil size={15} /></IconBtn>
                  <IconBtn testid={`photo-cover-${p.id}`} onClick={() => setCover(p.id)} title="Portada"><Star size={15} /></IconBtn>
                  <IconBtn testid={`photo-move-${p.id}`} onClick={() => setMoveId(p.id)} title="Mover"><Move size={15} /></IconBtn>
                </div>
                <div className="flex gap-1.5">
                  <IconBtn testid={`photo-replace-${p.id}`} onClick={() => { setReplacePid(p.id); replaceRef.current?.click(); }} title="Reemplazar"><RefreshCw size={15} /></IconBtn>
                  <IconBtn testid={`photo-delete-${p.id}`} danger onClick={() => setConfirmId(p.id)} title="Eliminar"><Trash2 size={15} /></IconBtn>
                </div>
                <span className="text-[10px] text-white/60 mt-1">Arrastra para reordenar</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => doReplace(e.target.files)} />

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Editar fotografía" testid="photo-edit-modal">
        {editing && (
          <>
            <img src={photoUrl(editing, true)} alt="" className="w-full max-h-52 object-contain rounded-md bg-[#0F0F10]" />
            <Field label="Título"><Input data-testid="photo-edit-title" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Descripción"><Textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-sm text-[#E4E4E7]">
              <input type="checkbox" data-testid="photo-edit-published" checked={editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} className="accent-[#C5A059]" /> Publicada (visible en el sitio)
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancelar</Btn>
              <Btn data-testid="photo-edit-save" onClick={saveEdit}>Guardar</Btn>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!moveId} onClose={() => setMoveId(null)} title="Mover a categoría" testid="photo-move-modal">
        <div className="grid grid-cols-2 gap-2">
          {categories.filter((c) => c.id !== activeCat).map((c) => (
            <Btn key={c.id} variant="subtle" data-testid={`move-target-${c.id}`} onClick={() => moveTo(c.id)}>{c.name}</Btn>
          ))}
        </div>
      </Modal>

      <Confirm open={!!confirmId} onClose={() => setConfirmId(null)} onConfirm={del} message="¿Eliminar esta fotografía? Esta acción no se puede deshacer." />

      {bulk && (
        <div data-testid="bulk-upload-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !bulkBusy && setBulk(null)}>
          <div className="bg-[#18181B] border border-[#27272A] rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#27272A]">
              <h3 className="font-serif text-xl text-white">Subida masiva · {bulk.length} foto(s)</h3>
              <button onClick={() => !bulkBusy && setBulk(null)} className="text-[#8C8375] hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 border-b border-[#27272A] flex flex-wrap items-center gap-3">
              <span className="text-sm text-[#A1A1AA]">Asignar la misma categoría a todas:</span>
              <select data-testid="bulk-assign-all" onChange={(e) => e.target.value && setAllCat(e.target.value)} defaultValue="" className="bg-[#0F0F10] border border-[#27272A] rounded-md text-sm text-white px-2 py-1.5 outline-none focus:border-[#C5A059]">
                <option value="">— elegir categoría —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="p-5 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 flex-1">
              {bulk.map((x, i) => (
                <div key={i} className="bg-[#0F0F10] border border-[#27272A] rounded-md overflow-hidden">
                  <img src={x.preview} alt="" className="w-full aspect-square object-cover" />
                  <select data-testid={`bulk-cat-${i}`} value={x.category_id || ""} onChange={(e) => setBulkCat(i, e.target.value)} className="w-full bg-[#18181B] border-t border-[#27272A] text-xs text-white px-2 py-2 outline-none focus:border-[#C5A059]">
                    <option value="">— categoría —</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[#27272A] flex justify-end gap-3">
              <button onClick={() => setBulk(null)} disabled={bulkBusy} className="px-4 py-2 rounded-md text-sm border border-[#3f3f46] text-[#E4E4E7]">Cancelar</button>
              <button data-testid="bulk-submit" onClick={submitBulk} disabled={bulkBusy} className="px-4 py-2 rounded-md text-sm font-medium bg-[#C5A059] text-[#0A0A0A] disabled:opacity-50">{bulkBusy ? "Subiendo…" : `Publicar ${bulk.length} foto(s)`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const IconBtn = ({ children, onClick, danger, title, testid }) => (
  <button data-testid={testid} title={title} onClick={onClick} className={`w-8 h-8 rounded-full flex items-center justify-center ${danger ? "bg-red-500/90 text-white hover:bg-red-500" : "bg-white/90 text-[#0A0A0A] hover:bg-[#C5A059]"} transition-colors`}>
    {children}
  </button>
);
